import { chargeCategoryLabelKeyMap } from 'src/components/pages/invoicing/CreateNewInvoice/constants';
import { isValidArrayIndex } from 'src/Library/Helper';
import { CInvoicingChargeDetailsType, CInvoicingChargeListType, CInvoicingChargeValueType, CInvoicingExchangeRatesType, CInvoicingPriceWithTaxType } from '../types';
import { CInvoicingChargeDetails } from './CInvoicingChargeDetails';
import { CInvoicingChargeValue } from './CInvoicingChargeValue';
import { CInvoicingPriceCurrency } from './CInvoicingPriceCurrency';
import { CInvoicingPriceUnitTax } from './CInvoicingPriceUnitTax';
import { CInvoicingPriceWithTax } from './CInvoicingPriceWithTax';

export class CInvoicingChargeList implements CInvoicingChargeListType {
  constructor(json: CInvoicingChargeListType) {
    Object.assign(this, json);
  }

  charges: (CInvoicingChargeValue | undefined)[];

  toInvoicingChargeDetails = (args: {
    referenceChargeDetails: CInvoicingChargeDetailsType;
    keysToCompare: (keyof CInvoicingChargeValueType)[];
    TaxValueMap: { [key: string]: number };
    exchangeRates: CInvoicingExchangeRatesType,
  }): CInvoicingChargeDetails => {
    const {
      referenceChargeDetails,
      keysToCompare,
      TaxValueMap,
      exchangeRates,
    } = args;
    const isRefChargesExist = !!referenceChargeDetails.details;
    const refChargesList = new CInvoicingChargeDetails(referenceChargeDetails).toInvoicingChargeList().charges;

    /**
     * Add added/modified charges to the lists
     */
    const mapCategoryToKey = (
      category?: string,
    ): 'originCharges' | 'freightCharges' | 'destinationCharges' | undefined => {
      if (!category) return undefined as any;
      // Primary mapping expects display labels like 'Freight Charges'
      const byLabel = (chargeCategoryLabelKeyMap as any)[category] as
        | 'originCharges'
        | 'freightCharges'
        | 'destinationCharges'
        | undefined;
      if (byLabel) return byLabel;
      // Fallback: support code-like categories e.g., 'FREIGHT', 'ORIGIN', 'DESTINATION'
      const norm = category.toUpperCase();
      if (norm === 'FREIGHT') return 'freightCharges' as any;
      if (norm === 'ORIGIN') return 'originCharges' as any;
      if (norm === 'DESTINATION') return 'destinationCharges' as any;
      return undefined as any;
    };

    const invoicingChargeDetails: any = this.charges.reduce(
      (acc: any, cur) => {
        const categoryKey = mapCategoryToKey(cur?.chargeCategory);
        let sameChargeInRefIdx: number = -1;
        if (categoryKey) {
          const idx = refChargesList?.findIndex((charge) => {
            const atLeastOnePropMismatch = keysToCompare.find(
              (key) => {
                const curChargeProp = cur?.[key] ?? null;
                const refChargeProp = charge?.[key] ?? null;
                return curChargeProp !== refChargeProp;
              },
            );
            const isAMatchingRefCharge = !atLeastOnePropMismatch;
            return isAMatchingRefCharge;
          });
          sameChargeInRefIdx = typeof idx === 'number' ? idx : -1;
        }
        const sameChargeInRef = isValidArrayIndex(sameChargeInRefIdx) ? refChargesList.splice(sameChargeInRefIdx, 1)[0] : undefined;
        const totalCurrent = cur ? new CInvoicingPriceUnitTax({
          pricePerUnit: cur.price,
          unitCount: cur.unitCount,
          taxPercent: TaxValueMap?.[cur.tax],
        }).toInvoicingPriceWithTax() : undefined;
        const totalReference = sameChargeInRef
          ? new CInvoicingPriceUnitTax({
            pricePerUnit: cur ? new CInvoicingPriceCurrency({
              price: sameChargeInRef?.price,
              sourceCurrency: sameChargeInRef?.currency,
              targetCurrency: cur?.currency,
            }).toInvoicingPriceAmount({
              exchangeRates,
            }).amount : 0,
            unitCount: sameChargeInRef?.unitCount,
            taxPercent: TaxValueMap?.[sameChargeInRef.tax],
          }).toInvoicingPriceWithTax()
          : undefined;

        const chargeValue = cur && totalCurrent ? {
          ...cur,
          dispute: cur.dispute,
          difference: totalCurrent.withTax - (totalReference?.withTax || 0),
          isNewCharge: isRefChargesExist && !sameChargeInRef,
          isRemovedCharge: false,
        } : undefined;
        if (categoryKey && chargeValue) {
          acc[categoryKey]?.values?.push(chargeValue);
        }
        return acc;
      },
      {
        originCharges: {
          name: 'Origin Charges',
          values: [] ,
        },
        freightCharges: {
          name: 'Freight Charges',
          values: [],
        },
        destinationCharges: {
          name: 'Destination Charges',
          values: [],
        },
      }
    );

    /**
     * Add removed charges to the lists
     */
    refChargesList.forEach(removedCharge => {
      const total = removedCharge
        ? new CInvoicingPriceUnitTax({
          pricePerUnit: removedCharge.price,
          unitCount: removedCharge.unitCount,
          taxPercent: TaxValueMap?.[removedCharge.tax],
        }).toInvoicingPriceWithTax()
        : undefined;
      const chargeCategory = mapCategoryToKey(removedCharge?.chargeCategory);
      if (chargeCategory) {
        invoicingChargeDetails[chargeCategory].values.push({
          ...removedCharge,
          isNewCharge: false,
          isRemovedCharge: true,
          difference: total?.withTax ? -total.withTax : 0,
        })
      }
    });
    return new CInvoicingChargeDetails({
      details: invoicingChargeDetails,
    });
  };

  toInvoicingPriceWithTax = (args: {
    exchangeRates: CInvoicingExchangeRatesType;
    TaxValueMap: { [key: string]: number };
    isPricePerUnit: boolean;
    isTaxDisabled: boolean;
    userDefaultCurrency?: string;
    orgCurrency?: string;
  }): CInvoicingPriceWithTax | undefined => {
    const {
      exchangeRates,
      TaxValueMap,
      isPricePerUnit,
      isTaxDisabled,
      userDefaultCurrency,
      orgCurrency,
    } = args;
    const total = this.charges ? this.charges.reduce((acc, charge) => {
      const {
        price,
        unitCount,
        tax,
        currency,
        hsnCode,
        name,
        difference,
        isRemovedCharge,
      } = charge || {};
      if (!price || !unitCount || isRemovedCharge) return acc;

      const rateToApply = exchangeRates?.rates?.find(rate =>
        rate?.sourceCurrency === currency && (orgCurrency ?
          rate?.targetCurrency === orgCurrency : true)
      );
      const conversionFactor = currency === orgCurrency ? 1 : (rateToApply?.targetAmount ? Number(rateToApply?.targetAmount) : undefined);
      const pricePerUnit = (conversionFactor ? price * conversionFactor : price) || 0;
      const unitCountEffective = isPricePerUnit ? 1 : (unitCount || 1);
      const taxPercent = isTaxDisabled ? 0 : (tax ? (TaxValueMap?.[tax] || 0) : 0);

      const lineTotal = (price && unitCount) ? new CInvoicingPriceUnitTax({
        pricePerUnit,
        unitCount: unitCountEffective,
        taxPercent,
      }).toInvoicingPriceWithTax() : null;

      if (!lineTotal) return;
      // acc.withTax += lineTotal?.withTax;
      acc.withoutTax += lineTotal?.withoutTax;
      acc.taxAmount += lineTotal?.taxAmount;
      return acc;
    }, {
      withoutTax: 0,
      taxAmount: 0,
    } as CInvoicingPriceWithTaxType) : null;

    if (total && userDefaultCurrency && (orgCurrency !== userDefaultCurrency)) {
      const rateToApply = exchangeRates?.rates?.find(rate =>
        rate?.sourceCurrency === orgCurrency && rate?.targetCurrency === userDefaultCurrency
      );

      if (rateToApply) {
        const conversionFactor = rateToApply?.targetAmount ? Number(rateToApply?.targetAmount) : undefined;
        total.withoutTax = (conversionFactor ? total.withoutTax * conversionFactor : total.withoutTax) || 0;
        total.taxAmount = (conversionFactor ? total.taxAmount * conversionFactor : total.taxAmount) || 0;
      }
    }

    // Instead of computing of total and then rounding off,
    //  we are computing without tax & tax amount, rounding those off,
    //  and adding those to get total.
    // This is done to be consistent with the back-end logic.  
    return total ? new CInvoicingPriceWithTax({
      ...total,
      withTax: total.withoutTax + total.taxAmount,
    }) : undefined;
  };
}
